import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import { forwardRef } from "react";
import * as RadixScrollArea from "@radix-ui/react-scroll-area";
import styled from "@emotion/styled";
import { useInView } from "react-intersection-observer";
import { css } from "@emotion/react";
import { composeRefs } from "@exonn/hooks";
import normalizeWheel from "normalize-wheel";

const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;

export const TabsScroll = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{
    onStartReached: (reached: boolean) => void;
    onEndReached: (reached: boolean) => void;
  }>
>((props, ref) => {
  const { children, onStartReached, onEndReached } = props;
  const [node, setNode] = useState<null | HTMLDivElement>(null);
  const setNodeCallback = useCallback((node: null | HTMLDivElement) => {
    setNode(node);
  }, []);

  useEffect(() => {
    if (node) {
      let wheelStepTimeout: NodeJS.Timeout | undefined = undefined;
      let wheelStep: null | number = null;

      const onWheel = (e: WheelEvent) => {
        const normalizedEvent = normalizeWheel(e);
        const currentX = node.scrollLeft;
        let delta = normalizedEvent.pixelY;
        const isVerticalScroll =
          Math.abs(normalizedEvent.pixelY) > Math.abs(normalizedEvent.pixelX);

        if (isVerticalScroll) {
          e.preventDefault();
        }

        if (isMac) {
          node.scrollLeft = currentX + delta;
        } else {
          clearTimeout(wheelStepTimeout);

          wheelStepTimeout = setTimeout(function () {
            wheelStep = null;
          }, 1000);

          if (!wheelStep) {
            wheelStep = Math.abs(delta);
          }
          delta = Math.round(delta / wheelStep);

          node.scrollLeft = currentX + delta * 30;
        }
      };
      node.addEventListener("wheel", onWheel);
      return () => {
        node.removeEventListener("wheel", onWheel);
      };
    }
  }, [node]);
  return (
    <Root type={"hover"}>
      <Viewport ref={composeRefs(ref, setNodeCallback)}>
        <Inner>
          <ScrollAreaEdgeIndicator
            scrollContainer={node}
            onReached={onStartReached}
            edge="start"
          />
          {children}
          <ScrollAreaEdgeIndicator
            scrollContainer={node}
            onReached={onEndReached}
            edge="end"
          />
        </Inner>
      </Viewport>

      <Scrollbar orientation="horizontal">
        <Thumb />
      </Scrollbar>
      <RadixScrollArea.Corner />
    </Root>
  );
});

type ScrollAreaEdgeIndicatorProps = {
  edge: "end" | "start";
  scrollContainer: HTMLDivElement | null;
  onReached: (reached: boolean) => void;
};

export const ScrollAreaEdgeIndicator = ({
  edge,
  scrollContainer,
  onReached,
}: ScrollAreaEdgeIndicatorProps) => {
  const { ref: connectIntersectionObserver } = useInView({
    root: scrollContainer,
    threshold: 0.05,
    onChange: (inView, entry) => {
      if (edge === "end") {
        onReached(inView);
      } else {
        onReached(inView);
      }
    },
  });

  return (
    <div
      css={indicatorStyles}
      data-edge={edge}
      ref={(node) => connectIntersectionObserver(node)}
    />
  );
};

const indicatorStyles = css`
  width: 1px;
  visibility: hidden;
  position: relative;

  &[data-edge="end"] {
    right: 1px;
    margin-right: -1px;
  }

  &[data-edge="start"] {
    left: 1px;
    margin-left: -1px;
  }
`;

const Viewport = styled(RadixScrollArea.Viewport)`
  position: relative;
  width: 100%;
`;

const Root = styled(RadixScrollArea.Root)`
  position: relative;
  width: 100%;
`;

const Inner = styled.div({
  display: "flex",
  overflow: "hidden",
});

const Thumb = styled(RadixScrollArea.Thumb)`
  background: ${({ theme }) => theme.colorsV2.grey[300]};
  top: 100%;
  user-select: none;
  // disable browser handling of all panning and zooming gestures on touch devices
  touch-action: none;
  padding: 1.5px;
  margin-top: 2px;
  border-radius: 4px;
`;

const Scrollbar = styled(RadixScrollArea.Scrollbar)`
  height: 5px;
  top: 100%;
`;
